import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";

import { isNullish } from "replo-utils/lib/misc";

import { getToolInputString, quotedText } from "../shared";
import { getToolPartOutputRecord } from "./bedrock-shared";

const DISPLAY_VALUE_MAX_LENGTH = 80;

function truncateDisplayValue({ value }: { value: string }) {
  return value.length > DISPLAY_VALUE_MAX_LENGTH
    ? `${value.slice(0, DISPLAY_VALUE_MAX_LENGTH - 3)}...`
    : value;
}

export function formatDisplayValue({ value }: { value: unknown }) {
  if (value === undefined || value === "") {
    return null;
  }

  try {
    const displayValue =
      typeof value === "string" ? value : JSON.stringify(value);
    return displayValue ? truncateDisplayValue({ value: displayValue }) : null;
  } catch {
    return null;
  }
}

function joinReadableList({ values }: { values: readonly string[] }) {
  if (values.length === 0) {
    return null;
  }

  if (values.length === 1) {
    return values[0] ?? null;
  }

  if (values.length === 2) {
    return values.join(" and ");
  }

  return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
}

export function getPreviewListDetail({
  listStyle = "readable",
  overflowLabel = "others",
  values,
}: {
  listStyle?: "comma" | "readable";
  overflowLabel?: "more" | "others";
  values: readonly string[];
}) {
  const overflowCount = values.length - 3;
  const overflowText =
    overflowLabel === "others" && overflowCount === 1 ? "other" : overflowLabel;
  const previewValues = [
    ...values.slice(0, 3),
    ...(overflowCount > 0 ? [`${overflowCount} ${overflowText}`] : []),
  ];

  return listStyle === "comma"
    ? previewValues.join(", ") || null
    : joinReadableList({ values: previewValues });
}

export function getAnalyticsQueryDetail({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}) {
  const query = getToolInputString({ properties: ["query"], toolPart });
  const outputRecord = getToolPartOutputRecord({ toolPart });
  const rowCount =
    typeof outputRecord?.rowCount === "number" ? outputRecord.rowCount : null;
  let rowCountDetail: string | null;
  if (isNullish(rowCount)) {
    rowCountDetail = null;
  } else if (rowCount === 0) {
    rowCountDetail = "and returned no rows";
  } else {
    rowCountDetail = `and returned ${rowCount === 1 ? "1 row" : `${rowCount} rows`}`;
  }

  return (
    [rowCountDetail, query ? `for ${quotedText({ value: query })}` : null]
      .filter((part): part is string => Boolean(part))
      .join(" ") || null
  );
}
